import { useEffect, useRef } from 'react';
import type { CameraSlice } from '../../camera';
import { useHandlers, useStoreApi } from '../../store';
import { createCanvasEventHub } from './CanvasEventHub';

/**
 * Attaches canvas event listeners to `document`.
 *
 * **Known limitation:** when multiple canvas instances coexist, all hubs
 * receive all events. Pointer/wheel is safe (`insideCanvas` guard), but
 * keyboard events dispatch to every hub. New keyboard handlers MUST
 * self-guard until this is fixed. See `MULTI_INSTANCE.md` in this
 * directory for full analysis and fix options.
 */
export const useCanvasEventHub = <TStore extends CameraSlice>(): void => {
  const store = useStoreApi<TStore>();
  const handlers = useHandlers<TStore>();

  const hubRef = useRef(
    createCanvasEventHub(handlers, store, {
      getViewport: () => store.getState().canvas.refs.viewportRef.current,
    })
  );

  useEffect(() => {
    return hubRef.current.mount();
  }, []);

  useEffect(() => {
    const hub = hubRef.current;

    const onPointer = (e: PointerEvent) => hub.handleDomPointerEvent(e);
    const onWheel = (e: WheelEvent) => hub.handleDomWheelEvent(e);
    const onKeyDown = (e: KeyboardEvent) => hub.handleDomKeyEvent(e, 'keyDown');
    const onKeyUp = (e: KeyboardEvent) => hub.handleDomKeyEvent(e, 'keyUp');

    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('pointermove', onPointer);
    document.addEventListener('pointerup', onPointer);
    document.addEventListener('pointercancel', onPointer);
    document.addEventListener('wheel', onWheel, { passive: true });
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);

    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('pointermove', onPointer);
      document.removeEventListener('pointerup', onPointer);
      document.removeEventListener('pointercancel', onPointer);
      document.removeEventListener('wheel', onWheel);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [store]);
};
